import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  MessageSquare, Send, Phone, CheckCircle, XCircle,
  AlertTriangle, Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SMSDashboard = () => {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendTo, setSendTo] = useState("");
  const [sendMsg, setSendMsg] = useState("");
  const [sending, setSending] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, logsRes] = await Promise.all([
        axios.get(`${API}/sms/status`, { withCredentials: true }),
        axios.get(`${API}/sms/log?limit=30`, { withCredentials: true }),
      ]);
      setStatus(statusRes.data);
      setLogs(logsRes.data.logs || []);
    } catch (err) {
      console.error("Failed to load SMS data", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sendSms = async () => {
    if (!sendTo || !sendMsg) return;
    setSending(true);
    try {
      await axios.post(`${API}/sms/send`, { to: sendTo, message: sendMsg }, { withCredentials: true });
      setSendMsg("");
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-zinc-500">Loading SMS data...</div>;

  return (
    <div className="space-y-4" data-testid="sms-dashboard">
      {/* Status */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-3 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${status?.configured ? "bg-green-900/30" : "bg-red-900/30"}`}>
              {status?.configured ? <CheckCircle className="w-5 h-5 text-green-400" /> : <AlertTriangle className="w-5 h-5 text-red-400" />}
            </div>
            <div>
              <p className="text-sm font-bold text-white">{status?.configured ? "Active" : "Not Configured"}</p>
              <p className="text-[10px] text-zinc-500">Twilio Status</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-blue-900/30">
              <Send className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-lg font-bold text-white">{status?.stats?.sent || 0}</p>
              <p className="text-[10px] text-zinc-500">SMS Sent</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-red-900/30">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-lg font-bold text-white">{status?.stats?.failed || 0}</p>
              <p className="text-[10px] text-zinc-500">Failed</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-amber-900/30">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-lg font-bold text-white">{status?.stats?.skipped || 0}</p>
              <p className="text-[10px] text-zinc-500">Skipped</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Setup Instructions (when not configured) */}
      {!status?.configured && (
        <Card className="bg-amber-900/20 border-amber-800/50">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-amber-400 mb-2">Setup Required</h3>
            <p className="text-xs text-amber-200/70 mb-2">Add these environment variables to enable SMS notifications:</p>
            <div className="bg-zinc-900 rounded p-3 font-mono text-xs text-zinc-300 space-y-1">
              <p>TWILIO_ACCOUNT_SID=ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx</p>
              <p>TWILIO_AUTH_TOKEN=your_auth_token</p>
              <p>TWILIO_PHONE_NUMBER=+1XXXXXXXXXX</p>
            </div>
            <p className="text-[10px] text-amber-200/50 mt-2">Get credentials at twilio.com/console. SMS will be logged but not sent until configured.</p>
          </CardContent>
        </Card>
      )}

      {/* Send SMS */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <Phone className="w-4 h-4 text-orange-400" /> Send SMS
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Input
            value={sendTo}
            onChange={(e) => setSendTo(e.target.value)}
            placeholder="Phone number (e.g., +14155552671)"
            className="bg-zinc-800 border-zinc-700 text-white"
            data-testid="sms-to-input"
          />
          <Textarea
            value={sendMsg}
            onChange={(e) => setSendMsg(e.target.value)}
            placeholder="Message..."
            className="bg-zinc-800 border-zinc-700 text-white min-h-[60px]"
            data-testid="sms-message-input"
          />
          <Button onClick={sendSms} disabled={sending || !sendTo || !sendMsg} className="bg-orange-600 hover:bg-orange-700" data-testid="send-sms-btn">
            <Send className="w-4 h-4 mr-1" /> {sending ? "Sending..." : "Send SMS"}
          </Button>
        </CardContent>
      </Card>

      {/* Templates */}
      {status?.templates && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm">Auto-Notification Templates</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {status.templates.map(t => (
                <div key={t} className="flex items-center gap-2 bg-zinc-800 rounded p-2">
                  <Badge className="bg-blue-900/30 text-blue-400 text-[10px] font-mono">{t}</Badge>
                  <span className="text-xs text-zinc-400">
                    {t === "order_confirmed" && "Sent when order is placed"}
                    {t === "in_production" && "Sent when production starts"}
                    {t === "shipped" && "Sent with tracking info"}
                    {t === "proof_ready" && "Sent when proof link is created"}
                    {t === "proof_approved" && "Sent when proof is approved"}
                    {t === "proof_changes" && "Sent when changes requested"}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* SMS Log */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-zinc-400" /> SMS Log
            <Badge className="bg-zinc-800 text-zinc-400 text-[10px]">{logs.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-xs text-zinc-600 py-4 text-center">No SMS messages logged yet</p>
          ) : (
            <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
              {logs.map((log, i) => (
                <div key={i} className="flex items-center justify-between bg-zinc-800 rounded p-2">
                  <div className="flex items-center gap-2 flex-1">
                    {log.status === "sent" && <CheckCircle className="w-3.5 h-3.5 text-green-400 shrink-0" />}
                    {log.status === "failed" && <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                    {log.status === "skipped" && <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-xs text-white font-mono truncate">{log.to}</p>
                      <p className="text-[10px] text-zinc-500 truncate">{log.body}</p>
                    </div>
                  </div>
                  <span className="text-[10px] text-zinc-600 shrink-0 ml-2">{new Date(log.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SMSDashboard;
